/**
 * Add / Edit vehicle modal.
 *
 * - Create mode (mode='create' or no `vehicle`): empty form, POSTs to
 *   /v1/vehicles via the parent's onSave.
 * - Edit mode (mode='edit' + vehicle): pre-fills, PUTs the fields.
 *
 * Captures the full inventory record: identity, spec (colour, drivetrain,
 * engine, doors/seats, rego + expiry, build/compliance), pricing (driveaway,
 * RRP, cost), a photo gallery (primary + additional URLs), a features list
 * and a marketing description. Money is captured in dollars and converted to
 * cents for the backend.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { vehiclesService } from '../lib/services';

const CONDITION_OPTIONS = [
  { value: 'NEW',  label: 'New' },
  { value: 'DEMO', label: 'Demo' },
  { value: 'USED', label: 'Used' },
];
const STATUS_OPTIONS = [
  { value: 'IN_STOCK',   label: 'In stock' },
  { value: 'RESERVED',   label: 'Reserved' },
  { value: 'SOLD',       label: 'Sold' },
  { value: 'IN_TRANSIT', label: 'In transit' },
  { value: 'ARCHIVED',   label: 'Archived' },
];
const TRANSMISSION_OPTIONS = ['Automatic', 'Manual', 'CVT', 'DCT'];
// Values MUST be the backend/DB enum values (uppercase) — the vehicles.fuel
// CHECK constraint is `fuel IN ('PETROL','DIESEL','HYBRID','PHEV','EV','LPG',
// 'UNKNOWN')`. Sending the title-case label ('Petrol') violated the constraint
// and surfaced as a confusing 409 "duplicate VIN/stock" on save. Labels stay
// friendly; only the wire value is canonical.
const FUEL_OPTIONS = [
  { value: 'PETROL', label: 'Petrol' },
  { value: 'DIESEL', label: 'Diesel' },
  { value: 'HYBRID', label: 'Hybrid' },
  { value: 'PHEV',   label: 'PHEV' },
  { value: 'EV',     label: 'EV' },
  { value: 'LPG',    label: 'LPG' },
];
const BODY_OPTIONS = ['SUV', 'UTE', 'HATCH', 'SEDAN', 'WAGON', 'VAN', 'COUPE', 'OTHER'];
const DRIVETRAIN_OPTIONS = ['FWD', 'RWD', 'AWD', '4X4'];

// Build / compliance are YYYY-MM month pickers. Bounds stop nonsense entries —
// a free-text box let a "2002-52" (month 52) through, which then tripped the
// backend. Earliest sane build month and a small future allowance for new
// stock whose compliance lands next model year.
const MONTH_MIN = '1950-01';
const MONTH_MAX = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
})();

const EMPTY = {
  make: '', model: '', trim: '', year: '', body: '', transmission: '',
  fuel: '', km: '', condition: 'NEW', driveAwayPriceDollars: '',
  rrpDollars: '', costPriceDollars: '', stockStatus: 'IN_STOCK', vin: '', dmsStockId: '',
  colourExterior: '', colourInterior: '', drivetrain: '', engineDescription: '',
  engineCapacityCc: '', cylinders: '', powerKw: '', doors: '', seats: '',
  rego: '', regoExpiry: '', buildDate: '', complianceDate: '',
  featuresText: '', description: '', primaryPhotoUrl: '', photoUrls: [],
};

function centsFromDollars(s) {
  if (s === '' || s == null) return null;
  const n = Number(String(s).replace(/[^\d.]/g, ''));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}
function dollarsFromCents(c) {
  if (c == null) return '';
  return String(Math.round(c / 100));
}
const numOrNull = (s) => (s === '' || s == null ? null : Number(String(s).replace(/[^\d]/g, '')) || null);

export default function VehicleModal({ open, mode = 'create', vehicle, onClose, onSave }) {
  const isEdit = mode === 'edit' && !!vehicle;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [newPhoto, setNewPhoto] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        make: vehicle.make || '', model: vehicle.model || '', trim: vehicle.trim || '',
        year: vehicle.year ?? '', body: vehicle.body || '', transmission: vehicle.transmission || '',
        fuel: vehicle.fuel || '', km: vehicle.km ?? '', condition: vehicle.condition || 'NEW',
        driveAwayPriceDollars: dollarsFromCents(vehicle.driveAwayPriceCents),
        rrpDollars: dollarsFromCents(vehicle.rrpCents),
        costPriceDollars: dollarsFromCents(vehicle.costPriceCents),
        stockStatus: vehicle.stockStatus || 'IN_STOCK',
        vin: vehicle.vin || '', dmsStockId: vehicle.dmsStockId || '',
        colourExterior: vehicle.colourExterior || '', colourInterior: vehicle.colourInterior || '',
        drivetrain: vehicle.drivetrain || '', engineDescription: vehicle.engineDescription || '',
        engineCapacityCc: vehicle.engineCapacityCc ?? '', cylinders: vehicle.cylinders ?? '',
        powerKw: vehicle.powerKw ?? '', doors: vehicle.doors ?? '', seats: vehicle.seats ?? '',
        rego: vehicle.rego || '', regoExpiry: vehicle.regoExpiry || '',
        buildDate: vehicle.buildDate || '', complianceDate: vehicle.complianceDate || '',
        featuresText: (vehicle.features || []).join('\n'),
        description: vehicle.description || '',
        primaryPhotoUrl: vehicle.primaryPhotoUrl || '',
        photoUrls: Array.isArray(vehicle.photoUrls) ? [...vehicle.photoUrls] : [],
      });
    } else {
      setForm(EMPTY);
    }
    setError(null); setBusy(false); setNewPhoto('');
  }, [open, isEdit, vehicle]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ---- photo gallery ----
  const addPhoto = () => {
    const url = newPhoto.trim();
    if (!url) return;
    setForm((f) => {
      const photoUrls = f.photoUrls.includes(url) ? f.photoUrls : [...f.photoUrls, url];
      const primaryPhotoUrl = f.primaryPhotoUrl || url;   // first added becomes primary
      return { ...f, photoUrls, primaryPhotoUrl };
    });
    setNewPhoto('');
  };
  const removePhoto = (url) => setForm((f) => {
    const photoUrls = f.photoUrls.filter((u) => u !== url);
    const primaryPhotoUrl = f.primaryPhotoUrl === url ? (photoUrls[0] || '') : f.primaryPhotoUrl;
    return { ...f, photoUrls, primaryPhotoUrl };
  });
  const makePrimary = (url) => setForm((f) => ({ ...f, primaryPhotoUrl: url }));

  // Upload one or more image files to the app's default storage, then add
  // the returned URLs to the gallery.
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true); setUploadError(null);
    try {
      const urls = await vehiclesService.uploadImages(files);
      const list = Array.isArray(urls) ? urls : [];
      setForm((f) => {
        const merged = [...f.photoUrls];
        for (const u of list) if (u && !merged.includes(u)) merged.push(u);
        const primaryPhotoUrl = f.primaryPhotoUrl || merged[0] || '';
        return { ...f, photoUrls: merged, primaryPhotoUrl };
      });
    } catch (e) {
      setUploadError(e?.response?.data?.message || e?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const canSubmit = useMemo(() => !busy && form.make.trim() && form.model.trim(), [busy, form.make, form.model]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError(null);
    try {
      const features = form.featuresText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const payload = {
        make: form.make.trim(), model: form.model.trim(), trim: form.trim.trim() || null,
        year: form.year === '' ? null : Number(form.year),
        body: form.body || null, transmission: form.transmission || null, fuel: form.fuel || null,
        km: form.km === '' ? null : Number(form.km),
        condition: form.condition || null,
        driveAwayPriceCents: centsFromDollars(form.driveAwayPriceDollars),
        rrpCents: centsFromDollars(form.rrpDollars),
        costPriceCents: centsFromDollars(form.costPriceDollars),
        stockStatus: form.stockStatus || null,
        vin: form.vin.trim() || null, dmsStockId: form.dmsStockId.trim() || null,
        colourExterior: form.colourExterior.trim() || null,
        colourInterior: form.colourInterior.trim() || null,
        drivetrain: form.drivetrain || null,
        engineDescription: form.engineDescription.trim() || null,
        engineCapacityCc: numOrNull(form.engineCapacityCc),
        cylinders: numOrNull(form.cylinders),
        powerKw: numOrNull(form.powerKw),
        doors: numOrNull(form.doors),
        seats: numOrNull(form.seats),
        rego: form.rego.trim() || null,
        regoExpiry: form.regoExpiry || null,
        buildDate: form.buildDate.trim() || null,
        complianceDate: form.complianceDate.trim() || null,
        features: features.length ? features : null,
        description: form.description.trim() || null,
        primaryPhotoUrl: form.primaryPhotoUrl.trim() || null,
        photoUrls: form.photoUrls.length ? form.photoUrls : null,
      };
      await onSave?.(payload, isEdit ? 'edit' : 'create');
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="veh-modal-title" onClick={onClose} className="modal-backdrop">
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ width: 'min(760px, 94vw)', maxHeight: '92vh', overflow: 'auto' }}>
        <div className="modal-header">
          <div>
            <div id="veh-modal-title" style={{ fontSize: 16, fontWeight: 600 }}>
              {isEdit ? `Edit · ${vehicle?.make} ${vehicle?.model}` : 'Add vehicle'}
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              {isEdit ? 'Override synced fields or complete missing details.' : 'Add a vehicle the stock feed hasn\'t pushed yet.'}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Section title="Identity">
            <Grid>
              <F label="Make *"><input value={form.make} onChange={(e) => setField('make', e.target.value)} placeholder="Toyota" autoFocus required /></F>
              <F label="Model *"><input value={form.model} onChange={(e) => setField('model', e.target.value)} placeholder="RAV4" required /></F>
              <F label="Trim / variant"><input value={form.trim} onChange={(e) => setField('trim', e.target.value)} placeholder="GXL" /></F>
              <F label="Year"><input value={form.year} onChange={(e) => setField('year', e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" maxLength={4} placeholder="2025" /></F>
              <F label="VIN"><input value={form.vin} onChange={(e) => setField('vin', e.target.value.toUpperCase())} maxLength={17} placeholder="17-char VIN" /></F>
              <F label="Stock #"><input value={form.dmsStockId} onChange={(e) => setField('dmsStockId', e.target.value)} placeholder="DMS stock id" /></F>
            </Grid>
          </Section>

          <Section title="Specification">
            <Grid>
              <F label="Body"><Select value={form.body} onChange={(v) => setField('body', v)} options={BODY_OPTIONS} /></F>
              <F label="Condition"><Select value={form.condition} onChange={(v) => setField('condition', v)} options={CONDITION_OPTIONS} noBlank /></F>
              <F label="Transmission"><Select value={form.transmission} onChange={(v) => setField('transmission', v)} options={TRANSMISSION_OPTIONS} /></F>
              <F label="Fuel"><Select value={form.fuel} onChange={(v) => setField('fuel', v)} options={FUEL_OPTIONS} /></F>
              <F label="Drivetrain"><Select value={form.drivetrain} onChange={(v) => setField('drivetrain', v)} options={DRIVETRAIN_OPTIONS} /></F>
              <F label="Odometer (km)"><input value={form.km} onChange={(e) => setField('km', e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" placeholder="0 for new" /></F>
              <F label="Exterior colour"><input value={form.colourExterior} onChange={(e) => setField('colourExterior', e.target.value)} placeholder="Eclipse Black" /></F>
              <F label="Interior colour"><input value={form.colourInterior} onChange={(e) => setField('colourInterior', e.target.value)} placeholder="Charcoal" /></F>
              <F label="Engine"><input value={form.engineDescription} onChange={(e) => setField('engineDescription', e.target.value)} placeholder="2.5L Hybrid 4cyl" /></F>
              <F label="Capacity (cc)"><input value={form.engineCapacityCc} onChange={(e) => setField('engineCapacityCc', e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" placeholder="2487" /></F>
              <F label="Cylinders"><input value={form.cylinders} onChange={(e) => setField('cylinders', e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" placeholder="4" /></F>
              <F label="Power (kW)"><input value={form.powerKw} onChange={(e) => setField('powerKw', e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" placeholder="131" /></F>
              <F label="Doors"><input value={form.doors} onChange={(e) => setField('doors', e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" placeholder="5" /></F>
              <F label="Seats"><input value={form.seats} onChange={(e) => setField('seats', e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" placeholder="5" /></F>
            </Grid>
          </Section>

          <Section title="Registration & dates">
            <Grid>
              <F label="Rego plate"><input value={form.rego} onChange={(e) => setField('rego', e.target.value.toUpperCase())} placeholder="1ABC234" /></F>
              <F label="Rego expiry"><input type="date" value={form.regoExpiry} onChange={(e) => setField('regoExpiry', e.target.value)} /></F>
              <F label="Build (YYYY-MM)"><input type="month" min={MONTH_MIN} max={MONTH_MAX} value={form.buildDate} onChange={(e) => setField('buildDate', e.target.value)} /></F>
              <F label="Compliance (YYYY-MM)"><input type="month" min={MONTH_MIN} max={MONTH_MAX} value={form.complianceDate} onChange={(e) => setField('complianceDate', e.target.value)} /></F>
            </Grid>
          </Section>

          <Section title="Pricing">
            <Grid>
              <F label="Driveaway (AUD)"><input value={form.driveAwayPriceDollars} onChange={(e) => setField('driveAwayPriceDollars', e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="58990" /></F>
              <F label="RRP (AUD)"><input value={form.rrpDollars} onChange={(e) => setField('rrpDollars', e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="62500" /></F>
              <F label="Cost / wholesale (AUD)"><input value={form.costPriceDollars} onChange={(e) => setField('costPriceDollars', e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="52000" /></F>
              <F label="Stock status"><Select value={form.stockStatus} onChange={(v) => setField('stockStatus', v)} options={STATUS_OPTIONS} noBlank /></F>
            </Grid>
          </Section>

          <Section title="Photos">
            <div
              onClick={() => !uploading && fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (!uploading) uploadFiles(e.dataTransfer.files); }}
              style={{
                border: '1.5px dashed var(--border, #cbd2d9)', borderRadius: 8, padding: 16,
                textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
                background: 'var(--surface-2, #fafbfc)', marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 20 }}>{uploading ? '⏳' : '⭱'}</div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {uploading ? 'Uploading…' : 'Click to upload photos, or drop image files here'}
              </div>
              <div className="text-xs text-muted">JPG, PNG, WebP or GIF · saved to your dealership's storage</div>
              <input
                ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={(e) => uploadFiles(e.target.files)}
              />
            </div>
            {uploadError && <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{uploadError}</div>}

            <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input value={newPhoto} onChange={(e) => setNewPhoto(e.target.value)} placeholder="…or paste an image URL" style={{ flex: 1 }}
                     onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPhoto(); } }} />
              <button type="button" className="btn small" onClick={addPhoto}>Add URL</button>
            </div>
            {form.photoUrls.length === 0 && <div className="text-xs text-muted">No photos yet — upload files above or paste a URL.</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {form.photoUrls.map((url) => (
                <div key={url} style={{ position: 'relative', width: 108 }}>
                  <img src={url} alt="" style={{ width: 108, height: 74, objectFit: 'cover', borderRadius: 6, border: form.primaryPhotoUrl === url ? '2px solid var(--accent-primary)' : '1px solid var(--border,#ddd)' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                    <button type="button" className="text-xs" style={{ background: 'none', border: 'none', cursor: 'pointer', color: form.primaryPhotoUrl === url ? 'var(--accent-primary)' : 'var(--text-secondary)' }} onClick={() => makePrimary(url)}>
                      {form.primaryPhotoUrl === url ? '★ Primary' : 'Set primary'}
                    </button>
                    <button type="button" className="text-xs" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }} onClick={() => removePhoto(url)}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Features & description">
            <F label="Features (one per line or comma-separated)">
              <textarea value={form.featuresText} onChange={(e) => setField('featuresText', e.target.value)} rows={3} placeholder={'Apple CarPlay\nAdaptive cruise\nHeated seats'} style={{ width: '100%' }} />
            </F>
            <F label="Description (listing copy)">
              <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} rows={3} placeholder="One owner, full service history…" style={{ width: '100%' }} />
            </F>
          </Section>

          {error && (
            <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', borderRadius: 6, color: 'var(--danger)', fontSize: 12 }}>{error}</div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Add vehicle')}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>{children}</div>;
}
function F({ label, children }) {
  return (
    <div className="form-field" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</label>
      {children}
    </div>
  );
}
function Select({ value, onChange, options, noBlank }) {
  const norm = (o) => (typeof o === 'string' ? { value: o, label: o } : o);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {!noBlank && <option value="">—</option>}
      {options.map((o) => { const n = norm(o); return <option key={n.value} value={n.value}>{n.label}</option>; })}
    </select>
  );
}
